import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, Stack, Chip, CircularProgress, Paper, TextField,
  InputAdornment, ToggleButton, ToggleButtonGroup, MenuItem, FormControlLabel,
  Switch, IconButton, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReleaseRow from '../components/releases/ReleaseRow';
import {
  toKstDate, todayKst, formatDate, groupByDate, buildMonthGrid, shiftMonth,
  productColor, WEEKDAYS,
} from '../utils/releaseView';

// FEAT-023 — 전사 릴리즈 모아보기.
//
// 🔴 이 화면의 목적은 "본부가 무엇을 언제 내보냈나" 를 한 자리에서 훑는 것이다.
//   지금까지는 주간 보고를 쓸 때마다 사람이 저장소를 하나씩 열어 손으로 모았다.
//
// 목록과 달력은 같은 데이터를 다르게 본다 — 목록은 "무엇이" 를, 달력은 "언제·얼마나
// 몰렸나" 를 답한다. 둘 중 하나만 두면 다른 질문에 답할 수 없어 둘 다 둔다.
//
// 데이터: GET /api/releases (매일 12:40 KST 수집 스냅샷)

export default function ReleasesPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [hint, setHint] = useState(null);
  // 기본은 달력이다 — 이 화면의 첫 질문이 "언제 얼마나 나갔나" 이고, 그건 달력이 먼저 답한다.
  const [view, setView] = useState('calendar');
  const [search, setSearch] = useState('');
  const [product, setProduct] = useState('all');
  const [showPre, setShowPre] = useState(false);
  const [month, setMonth] = useState(() => todayKst().slice(0, 7));
  const [pickedDate, setPickedDate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/releases')
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw Object.assign(new Error(body.error || `HTTP ${r.status}`), { hint: body.hint });
        return body;
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) { setErr(e.message); setHint(e.hint || null); } });
    return () => { cancelled = true; };
  }, []);

  const productNames = useMemo(
    () => new Map((data?.products || []).map((p) => [p.id, p.name])),
    [data],
  );

  // 제품·사전배포·본문 검색 필터. 날짜(달력 칸)는 아래에서 따로 적용한다 —
  // 달력 자체는 날짜 필터 이전 집합으로 그려야 다른 날에 무엇이 있는지 보인다.
  const filtered = useMemo(() => {
    const list = data?.releases || [];
    const q = search.trim().toLowerCase();
    return list.filter((r) => {
      if (!showPre && r.prerelease) return false;
      if (product === 'unassigned') {
        if ((r.products || []).length > 0) return false;
      } else if (product !== 'all' && !(r.products || []).includes(product)) return false;
      if (!q) return true;
      return (
        r.repo.toLowerCase().includes(q) ||
        (r.tag || '').toLowerCase().includes(q) ||
        (r.title || '').toLowerCase().includes(q) ||
        (r.body || '').toLowerCase().includes(q)
      );
    });
  }, [data, search, product, showPre]);

  const byDate = useMemo(() => groupByDate(filtered), [filtered]);
  const visible = useMemo(
    () => (pickedDate ? filtered.filter((r) => toKstDate(r.published) === pickedDate) : filtered),
    [filtered, pickedDate],
  );

  if (err) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>릴리즈</Typography>
        <Alert severity="warning">
          {err}
          {hint && <Box sx={{ mt: 0.5, fontSize: 13, opacity: 0.85 }}>{hint}</Box>}
        </Alert>
      </Box>
    );
  }
  if (!data) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={18} /> <Typography variant="body2">릴리즈 현황 불러오는 중…</Typography>
      </Box>
    );
  }

  const c = data.counts || {};
  const monthCells = buildMonthGrid(month);
  const maxInDay = Math.max(1, ...[...byDate.values()].map((v) => v.length));

  return (
    <Box sx={{ p: 3 }}>
      {/* 제목·집계는 왼쪽, 수집 시각은 오른쪽 끝. 한 줄로 묶어 본문 시작 위치를 끌어올린다. */}
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="h5">릴리즈</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          제품 {c.products} · 저장소 {c.repos_with_releases}/{c.repos_ok} · 릴리즈 {c.releases}건
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="매일 12:40 KST 갱신">
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            수집 {data.generated_at ? new Date(data.generated_at).toLocaleString('ko-KR') : '-'}
          </Typography>
        </Tooltip>
      </Stack>

      {c.repos_failed > 0 && (
        <Alert severity="info" sx={{ mt: 1.5, py: 0.25 }}>
          수집하지 못한 저장소 {c.repos_failed}개가 있습니다 — 해당 저장소의 릴리즈는 목록에 없습니다.
          {' '}({(data.failures || []).slice(0, 3).map((f) => `${f.repo}(${f.reason})`).join(', ')}
          {(data.failures || []).length > 3 ? ' 외' : ''})
        </Alert>
      )}

      {/* 필터 */}
      <Paper variant="outlined" sx={{ p: 1.5, mt: 2, mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
          >
            <ToggleButton value="calendar"><CalendarMonthIcon fontSize="small" sx={{ mr: 0.5 }} />달력</ToggleButton>
            <ToggleButton value="list"><ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />목록</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            select size="small" label="제품" value={product}
            onChange={(e) => { setProduct(e.target.value); setPickedDate(null); }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">전체</MenuItem>
            {(data.products || []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
            <MenuItem value="unassigned">제품 미지정</MenuItem>
          </TextField>

          <TextField
            size="small" placeholder="저장소·버전·노트 본문 검색"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />

          <FormControlLabel
            control={<Switch size="small" checked={showPre} onChange={(e) => setShowPre(e.target.checked)} />}
            label={<Typography variant="body2">사전 배포 포함</Typography>}
          />
        </Stack>
      </Paper>

      {view === 'calendar' && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 1.5 }}>
            <IconButton size="small" onClick={() => { setMonth(shiftMonth(month, -1)); setPickedDate(null); }}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 110, textAlign: 'center' }}>
              {month.replace('-', '년 ')}월
            </Typography>
            <IconButton size="small" onClick={() => { setMonth(shiftMonth(month, 1)); setPickedDate(null); }}>
              <ChevronRightIcon />
            </IconButton>
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {WEEKDAYS.map((d) => (
              <Typography key={d} variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', pb: 0.5 }}>
                {d}
              </Typography>
            ))}
            {monthCells.map((ymd, i) => {
              if (!ymd) return <Box key={`e${i}`} />;
              const items = byDate.get(ymd) || [];
              const isToday = ymd === todayKst();
              const picked = ymd === pickedDate;
              return (
                <Box
                  key={ymd}
                  onClick={() => setPickedDate(picked ? null : ymd)}
                  sx={{
                    minHeight: 68, p: 0.5, borderRadius: '4px', cursor: items.length ? 'pointer' : 'default',
                    border: 1,
                    borderColor: picked ? 'primary.main' : isToday ? 'warning.main' : 'divider',
                    bgcolor: items.length ? `rgba(88,166,255,${0.06 + 0.18 * (items.length / maxInDay)})` : 'transparent',
                    '&:hover': items.length ? { borderColor: 'primary.main' } : undefined,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: isToday ? 700 : 400 }}>
                    {Number(ymd.slice(8))}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.25, mt: 0.25 }}>
                    {items.slice(0, 6).map((r, idx) => (
                      <Tooltip key={idx} title={`${r.repo} ${r.tag}`}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: productColor((r.products || [])[0]) }} />
                      </Tooltip>
                    ))}
                    {items.length > 6 && (
                      <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary' }}>+{items.length - 6}</Typography>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {pickedDate && (
        <Chip
          sx={{ mb: 1.5 }}
          label={`${formatDate(pickedDate)} 만 보는 중 — 해제`}
          onDelete={() => setPickedDate(null)}
          color="primary"
          variant="outlined"
        />
      )}

      <Paper variant="outlined">
        {visible.length === 0 ? (
          <Typography variant="body2" sx={{ p: 3, color: 'text.secondary', textAlign: 'center' }}>
            조건에 맞는 릴리즈가 없습니다.
            {!showPre && (data.counts?.prerelease > 0) && ' 사전 배포를 포함해 보세요.'}
          </Typography>
        ) : (
          <>
            <Typography variant="caption" sx={{ display: 'block', px: 1.5, py: 1, color: 'text.secondary', borderBottom: 1, borderColor: 'divider' }}>
              {visible.length}건
            </Typography>
            {visible.slice(0, 300).map((r) => (
              <ReleaseRow key={`${r.repo}@${r.tag}`} release={r} productNames={productNames} />
            ))}
            {visible.length > 300 && (
              <Typography variant="caption" sx={{ display: 'block', p: 1.5, color: 'text.secondary' }}>
                … 300건까지 표시합니다. 필터로 좁혀 주세요.
              </Typography>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
